/**
 * Cost Summary Cards Component
 * Displays the Total Cost Impact, Non-Compliance Risk, and Over-Compliance Cost cards
 */
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, AlertTriangle, TrendingUp } from "lucide-react";

const CostSummaryCards = ({ 
  grandTotal, 
  totalRisk, 
  totalOverpay, 
  nonComplianceRisk, 
  overComplianceCost,
  formatCurrency, 
  getPeriodLabel 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Total Cost */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Cost Impact</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(grandTotal)}</p>
              <Badge variant="outline" className="text-xs mt-1">{getPeriodLabel()}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Non-Compliance Risk */}
      <Card className="border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Non-Compliance Risk</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalRisk)}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="text-center p-2 bg-background/50 rounded">
              <p className="text-muted-foreground">Damages</p>
              <p className="font-semibold">{formatCurrency(nonComplianceRisk.baseDamages)}</p>
            </div>
            <div className="text-center p-2 bg-background/50 rounded">
              <p className="text-muted-foreground">Liquidated</p>
              <p className="font-semibold">{formatCurrency(nonComplianceRisk.liquidatedDamages)}</p>
            </div>
            <div className="text-center p-2 bg-background/50 rounded">
              <p className="text-muted-foreground">Penalties</p>
              <p className="font-semibold">{formatCurrency(nonComplianceRisk.civilPenalties)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Over-Compliance Cost */}
      <Card className="border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Over-Compliance Cost</p>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalOverpay)}</p>
            </div>
          </div>
          <div className="mt-3 p-2 bg-background/50 rounded text-xs text-center">
            <p className="text-muted-foreground">Unnecessary Extra Payments</p>
            <p className="font-semibold">{formatCurrency(overComplianceCost.extraPayments)}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostSummaryCards;
